import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupportTicket, useReplySupportTicket } from '../hooks/useApi';
import type { TicketReply } from '../types';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  'in-progress': 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600'
};

function ReplyBubble({ reply }: { reply: TicketReply }) {
  const { t } = useTranslation('support');
  const formatDate = useFormatDate();
  const isAdmin = reply.from === 'admin';
  return (
    <div className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] rounded-lg p-4 ${isAdmin ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50 border border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${isAdmin ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-700'}`}>
            {isAdmin ? t('ticket.supportLabel') : t('ticket.you')}
          </span>
          <span className="text-xs text-gray-400">{reply.timestamp ? formatDate(reply.timestamp, 'datetime') : '\u2014'}</span>
        </div>
        <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">{reply.message}</div>
      </div>
    </div>
  );
}

export function SupportTicketPage() {
  const { t } = useTranslation('support');
  const formatDate = useFormatDate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState('');

  const { data: ticket, isLoading, error } = useSupportTicket(ticketId!);
  const replyMutation = useReplySupportTicket(ticketId!);

  const handleReply = () => {
    replyMutation.mutate(replyText, {
      onSuccess: () => setReplyText('')
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-full p-4 sm:p-8">
        <div className="page-narrow">
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-full p-4 sm:p-8">
        <div className="page-narrow">
          <button onClick={() => navigate('/support')} className="text-sm font-medium text-primary-600 hover:text-primary-700">
            &larr; {t('ticket.backToSupport')}
          </button>
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : t('ticket.ticketNotFound')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
    <div className="mx-auto max-w-3xl space-y-4">
      <button onClick={() => navigate('/support')} className="text-sm font-medium text-primary-600 hover:text-primary-700">
        &larr; {t('ticket.backToSupport')}
      </button>

      {/* Ticket header */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{ticket.subject}</h1>
            <p className="mt-1 font-mono text-xs text-gray-400">{ticket.ticketId}</p>
          </div>
          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[ticket.status] ?? STATUS_STYLES.open}`}>
            {ticket.status}
          </span>
        </div>
        <div className="mt-3 flex gap-4 text-xs text-gray-400">
          <span className="capitalize">{ticket.category}</span>
          <span>{ticket.createdAt ? t('ticket.created', { date: formatDate(ticket.createdAt, 'datetime') }) : '\u2014'}</span>
        </div>
      </div>

      {/* Conversation */}
      <div className="space-y-3">
        {/* Original message */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-lg bg-gray-50 border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">{t('ticket.you')}</span>
              <span className="text-xs text-gray-400">{ticket.createdAt ? formatDate(ticket.createdAt, 'datetime') : '\u2014'}</span>
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">{ticket.message}</div>
          </div>
        </div>

        {/* Replies */}
        {ticket.replies && ticket.replies.map((r, i) => <ReplyBubble key={i} reply={r} />)}
      </div>

      {/* Reply form */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs text-gray-400 mb-3">
          {ticket.status === 'closed'
            ? t('ticket.closedNote')
            : ticket.status === 'resolved'
            ? t('ticket.resolvedNote')
            : t('ticket.openNote')}
        </p>
        {replyMutation.isError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {replyMutation.error instanceof Error ? replyMutation.error.message : t('ticket.failedToSend')}
          </div>
        )}

        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder={t('ticket.replyPlaceholder')}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
        />
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-gray-400">{t('ticket.charCount', { count: replyText.length })}</p>
          <button
            onClick={handleReply}
            disabled={replyMutation.isPending || !replyText.trim()}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {replyMutation.isPending ? t('ticket.sending') : t('ticket.sendReply')}
          </button>
        </div>
      </div>
    </div>
    </div>
  );
}
